import { api } from 'lib/httpClient';
import {
  useMutation,
  useQueryClient,
  useQuery,
  keepPreviousData,
} from '@tanstack/react-query';

const QUERY_CACHE_KEY = 'investor-reporting';

async function fetchCustomUpdate({ queryKey }) {
  const [, id] = queryKey;
  const { data: response } = await api.get(`custom-update/${id}`);
  return response.data.customUpdate;
}

async function createCustomUpdate(data) {
  const response = await api.post(`custom-update/`, data);
  return response;
}

async function updateCustomUpdate(data) {
  const response = await api.put(`custom-update/${data._id}`, data);
  return response;
}

async function deleteCustomUpdate(id: string) {
  const response = await api.delete(`custom-update/${id}`);
  return response;
}

function useCreateCustomUpdateAPI() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCustomUpdate,
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_CACHE_KEY],
      });
      // Also invalidate individual custom update caches
      queryClient.invalidateQueries({
        queryKey: ['custom-update'],
      });
    },
  });
}

function useUpdateCustomUpdateAPI() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateCustomUpdate,
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_CACHE_KEY],
      });
      // Also invalidate individual custom update caches
      queryClient.invalidateQueries({
        queryKey: ['custom-update'],
      });
    },
  });
}

type DeleteCustomUpdateAPIProps = {
  onDeleteSuccess?: () => void;
};

function useDeleteCustomUpdateAPI({
  onDeleteSuccess,
}: DeleteCustomUpdateAPIProps = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCustomUpdate,
    onSuccess: async () => {
      onDeleteSuccess?.();
      queryClient.invalidateQueries({
        queryKey: [QUERY_CACHE_KEY],
      });
      // Also invalidate individual custom update caches
      queryClient.invalidateQueries({
        queryKey: ['custom-update'],
      });
    },
  });
}

function useFetchCustomUpdateAPI(id) {
  return useQuery({
    queryKey: ['custom-update', id],
    queryFn: fetchCustomUpdate,
    placeholderData: keepPreviousData,
    enabled: !!id, // Only run the query if id is truthy
  });
}

type UseCustomUpdateProps = {
  onDeleteSuccess?: () => void;
};

export function useCustomUpdate({
  onDeleteSuccess,
}: UseCustomUpdateProps = {}) {
  return {
    createCustomUpdate: useCreateCustomUpdateAPI(),
    updateCustomUpdate: useUpdateCustomUpdateAPI(),
    deleteCustomUpdate: useDeleteCustomUpdateAPI({ onDeleteSuccess }),
    fetchCustomUpdate: useFetchCustomUpdateAPI,
  };
}
